import { Box, Button } from '@mui/material'

import React, { useMemo } from 'react'
import { useSchoolColumns } from './SchoolCells'
import { List } from 'infrastructure/api'
import { Table } from 'shared/ui/Table/Table'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  canAddPackageToSchoolsAtom,
  canRemovePackageFromSchoolsAtom,
  selectedActionAtom,
  selectedSchoolsAtom,
} from 'shared/api/platformFeatures'

type SchoolFeaturesSectionProps = {
  schools: List.School[]
}

export const SchoolFeaturesSection = ({
  schools,
}: SchoolFeaturesSectionProps) => {
  const schoolColumns = useSchoolColumns()
  const { selectedIndexes, onIndexSelect } = useIndexSelection(schools)
  const toolBarItems = useToolBarItems()

  if (!schools.length) return null

  return (
    <Box className="accordion-element">
      <Table
        columns={schoolColumns}
        items={schools}
        onRowSelect={onIndexSelect}
        selectedRows={selectedIndexes}
        overrideHeight={300}
        toolBarItems={toolBarItems}
      />
    </Box>
  )
}

function useToolBarItems() {
  const setSelectedAction = useSetAtom(selectedActionAtom)
  const canAdd = useAtomValue(canAddPackageToSchoolsAtom)
  const canRemove = useAtomValue(canRemovePackageFromSchoolsAtom)

  return useMemo(() => {
    const addAction = () =>
      setSelectedAction({
        actionType: 'add',
        entityType: 'school',
      })
    const removeAction = () =>
      setSelectedAction({
        actionType: 'remove',
        entityType: 'school',
      })
    return [
      <div className="section-buttons" key="add">
        <Button onClick={addAction} variant="outlined" disabled={!canAdd}>
          Add
        </Button>
      </div>,
      <div className="section-buttons" key="remove">
        <Button
          variant="outlined"
          color="primary"
          onClick={removeAction}
          disabled={!canRemove}
        >
          Remove
        </Button>
      </div>,
    ]
  }, [setSelectedAction, canAdd, canRemove])
}

function useIndexSelection(schools: List.School[]) {
  const [selectedSchools, setSelectedSchools] = useAtom(selectedSchoolsAtom)
  return useMemo(() => {
    const selectedIndexes = schools.reduce<number[]>((acc, school, index) => {
      if (selectedSchools.includes(school)) {
        acc.push(index)
      }
      return acc
    }, [])
    const onIndexSelect = (index: number) => {
      if (index === -1) {
        if (selectedSchools.length === schools.length) {
          setSelectedSchools([])
        } else {
          setSelectedSchools(schools)
        }
      } else {
        const school = schools[index]
        if (selectedSchools.includes(school)) {
          setSelectedSchools(selectedSchools.filter((s) => s !== school))
        } else {
          setSelectedSchools([...selectedSchools, school])
        }
      }
    }
    return { selectedIndexes, onIndexSelect }
  }, [schools, selectedSchools, setSelectedSchools])
}
